import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { Ayuda } from '../../components/Ayuda';
import { Spinner } from '../../components/Spinner';
import { nf, type Favorito } from './tipos';

// ---- e-SEIA (2026-10-19): buscar expedientes aprobados y traerlos como caso de referencia ----
interface CatalogoSeia { regiones: { id: string; nombre: string }[]; comunas: { id: string; nombre: string; region_id: string }[]; estados: { id: string; nombre: string }[] }
interface FilaSeia { expedienteId: string; nombre: string; tipo: string; region: string; comunas: string[]; tipologia: string | null; titular: string | null; inversionMM: number | null; fechaPresentacion: string | null; estado: string; urlFicha: string }
type TipoSeia = 'DIA' | 'EIA' | 'Ambos';
const ESTADO_APROBADO = '4';

/** aaaa-mm-dd → dd/mm/aaaa (sin pasar por Date: evita corrimientos de zona horaria). */
const fechaCL = (iso: string | null) => {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

/** Mensajes de error del conector e-SEIA, por código HTTP. */
const mensajeSeia = (e: unknown, generico: string) => {
  if (e instanceof ApiError) {
    if (e.status === 409) return 'Ese expediente ya está como referencia.';
    if (e.status === 502) return 'El e-SEIA no respondió, intenta de nuevo.';
  }
  return generico;
};

export function ModalSeia({ yaAgregados, onAgregado, onCerrar }: { yaAgregados: Set<string>; onAgregado: () => Promise<void>; onCerrar: () => void }) {
  const [catalogo, setCatalogo] = useState<CatalogoSeia | null>(null);
  const [catalogoError, setCatalogoError] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [regionId, setRegionId] = useState('');
  const [comunaId, setComunaId] = useState('');
  const [tipo, setTipo] = useState<TipoSeia>('DIA');
  const [buscando, setBuscando] = useState(false);
  const [buscarError, setBuscarError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{ total: number; filas: FilaSeia[] } | null>(null);
  // Estado por expediente: agregando | agregada | mensaje de error.
  const [estadoFila, setEstadoFila] = useState<Record<string, 'agregando' | 'agregada' | { error: string }>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<CatalogoSeia>('/api/sharepoint/seia/catalogos')
      .then(setCatalogo)
      .catch((e) => setCatalogoError(mensajeSeia(e, 'No se pudieron cargar las regiones y comunas.')));
  }, []);

  const comunas = (catalogo?.comunas ?? []).filter((c) => !regionId || c.region_id === regionId);

  async function buscar() {
    setBuscando(true); setBuscarError(null); setAviso(null);
    try {
      const q = new URLSearchParams({ estadoId: ESTADO_APROBADO, tipo, pagina: '1' });
      if (texto.trim()) q.set('texto', texto.trim());
      if (regionId) q.set('regionId', regionId);
      if (comunaId) q.set('comunaId', comunaId);
      setResultado(await apiFetch<{ total: number; filas: FilaSeia[] }>(`/api/sharepoint/seia/buscar?${q.toString()}`));
    } catch (e) {
      setResultado(null);
      setBuscarError(mensajeSeia(e, 'No se pudo buscar en el e-SEIA.'));
    } finally { setBuscando(false); }
  }

  async function agregar(f: FilaSeia) {
    setEstadoFila((s) => ({ ...s, [f.expedienteId]: 'agregando' }));
    setAviso(null);
    try {
      await apiFetch<{ favorito: Favorito }>('/api/sharepoint/favoritos-seia', {
        method: 'POST',
        body: JSON.stringify({ expedienteId: f.expedienteId, nombre: `Caso ${f.nombre} (${f.titular ?? 'titular no informado'}) — e-SEIA` }),
      });
      setEstadoFila((s) => ({ ...s, [f.expedienteId]: 'agregada' }));
      setAviso(`Referencia agregada: ${f.nombre}. Andrés empezará a estudiarla ahora.`);
      await onAgregado();
    } catch (e) {
      setEstadoFila((s) => ({ ...s, [f.expedienteId]: { error: mensajeSeia(e, 'No se pudo agregar la referencia.') } }));
    }
  }

  const BotonAgregar = ({ f }: { f: FilaSeia }) => {
    const st = estadoFila[f.expedienteId];
    if (st === 'agregada' || (st === undefined && yaAgregados.has(f.expedienteId))) {
      return <span className="chip bg-emerald-50 text-emerald-800 ring-emerald-300"><span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Agregada</span>;
    }
    return (
      <span className="flex flex-col items-start gap-1 sm:items-end">
        <span className="inline-flex items-center">
          <button type="button" onClick={() => void agregar(f)} disabled={st === 'agregando'} className="btn-secundario !h-8 whitespace-nowrap px-3 text-xs">
            {st === 'agregando' ? <><Spinner tamano="xs" /> Agregando…</> : 'Agregar como referencia'}
          </button>
          <Ayuda texto="Qué se descarga: solo la DIA/EIA con sus capítulos y anexos, adendas, ICSARA, ICE, RCA y los oficios de pronunciamiento de cada servicio. No se baja el expediente completo. Los documentos escaneados quedan sin texto hasta que pidas OCR (Iris). Cada consulta queda auditada." />
        </span>
        {typeof st === 'object' && <span role="alert" className="text-[11px] font-normal text-red-700">{st.error}</span>}
      </span>
    );
  };

  const filas = resultado?.filas ?? [];

  return (
    <Modal titulo="Agregar desde e-SEIA" onCerrar={onCerrar} ancho="xl" ayuda="Qué es el e-SEIA: el portal público del Servicio de Evaluación Ambiental con todos los proyectos evaluados en Chile (DIA y EIA). Desde aquí traes expedientes como casos de referencia para entrenar a los agentes.">
      <p className="-mt-3 mb-4 text-[13px] font-normal text-gris">Busca proyectos <span className="font-medium text-texto">aprobados</span> en el e-SEIA y agrégalos como casos de referencia para entrenar a Andrés.</p>

      {catalogoError && <p role="alert" className="alerta-error mb-3">{catalogoError}</p>}

      <form onSubmit={(e) => { e.preventDefault(); void buscar(); }} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1.25fr_1fr_1fr_auto]">
        <div className="sm:col-span-2 lg:col-span-1">
          <div className="flex items-center"><label className="etiqueta !mb-0" htmlFor="seia-texto">Nombre del proyecto</label><Ayuda texto="Busca por una palabra del nombre; el buscador del SEA acepta acentos y ñ (por ejemplo Huañil)." /></div>
          <input id="seia-texto" value={texto} onChange={(e) => setTexto(e.target.value)} className="campo mt-2.5" placeholder="Opcional · p. ej. Pampino" autoComplete="off" />
        </div>
        <div>
          <div className="flex items-center"><label className="etiqueta !mb-0" htmlFor="seia-region">Región</label><Ayuda texto="Cómo elegir: parte por la comuna del proyecto que estás trabajando; los instrumentos (PLADECO, PRC, políticas regionales) se comparten entre proyectos de la misma comuna y región." /></div>
          <select id="seia-region" value={regionId} onChange={(e) => { setRegionId(e.target.value); setComunaId(''); }} className="campo mt-2.5" disabled={!catalogo}>
            <option value="">Todas las regiones</option>
            {catalogo?.regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="etiqueta !mb-0 flex h-5 items-center" htmlFor="seia-comuna">Comuna</label>
          <select id="seia-comuna" value={comunaId} onChange={(e) => setComunaId(e.target.value)} className="campo mt-2.5" disabled={!catalogo || !regionId}>
            <option value="">{regionId ? 'Todas las comunas' : 'Elige una región primero'}</option>
            {comunas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1 lg:w-28 lg:flex-none">
            <label className="etiqueta !mb-0 flex h-5 items-center" htmlFor="seia-tipo">Tipo</label>
            <select id="seia-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoSeia)} className="campo mt-2.5">
              <option value="DIA">DIA</option>
              <option value="EIA">EIA</option>
              <option value="Ambos">Ambos</option>
            </select>
          </div>
          <button type="submit" disabled={buscando || !catalogo} className="btn-primario h-11 shrink-0">
            {buscando ? <><Spinner tamano="sm" tono="blanco" /> Buscando…</> : 'Buscar'}
          </button>
        </div>
      </form>
      <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11.5px] font-normal text-gris">
        <span>Estado:</span>
        <span className="chip bg-emerald-50 text-emerald-800 ring-emerald-300">Aprobado</span>
        <Ayuda texto="Por qué solo aprobados: son los proyectos que pasaron la evaluación completa; sus capítulos, oficios de los servicios, ICSARA, ICE y RCA enseñan qué se exigió y cómo se resolvió." className="!ml-0" />
        <span>· Consulta en vivo al SEA, una petición por segundo para respetar su sitio; puede tardar unos segundos.</span>
      </div>

      {aviso && <p role="status" className="mt-4 rounded-lg bg-emerald-50/70 px-3 py-2 text-sm font-normal text-emerald-900 ring-1 ring-inset ring-emerald-200">✓ {aviso}</p>}
      {buscarError && <p role="alert" className="alerta-error mt-4">{buscarError}</p>}

      {!catalogo && !catalogoError && (
        <div role="status" aria-live="polite" className="mt-6 flex items-center justify-center gap-3 py-6"><Spinner tamano="md" /><span className="text-[13px] font-normal text-gris">Cargando regiones y comunas…</span></div>
      )}

      {buscando && (
        <div role="status" aria-live="polite" className="mt-6 flex items-center justify-center gap-3 py-8"><Spinner tamano="md" /><span className="text-[13px] font-normal text-gris">Consultando el e-SEIA…</span></div>
      )}

      {!buscando && resultado && (
        <div className="mt-5" aria-live="polite">
          <p className="text-[13px] font-medium text-azul-noche">
            {nf.format(resultado.total)} {resultado.total === 1 ? 'proyecto encontrado' : 'proyectos encontrados'}
            {resultado.total > filas.length && <span className="font-normal text-gris"> · mostrando los primeros {filas.length}; acota por región o comuna</span>}
          </p>
          {filas.length === 0 && <p className="mt-2 text-sm font-normal text-gris">Prueba con otro nombre, región o tipo.</p>}

          {/* Móvil: cards */}
          <ul className="mt-3 flex max-h-[55vh] flex-col gap-2 overflow-y-auto sm:hidden">
            {filas.map((f) => (
              <li key={f.expedienteId} className="tarjeta p-3.5">
                <p className="text-[13px] font-medium text-azul-noche">{f.nombre}</p>
                <p className="mt-0.5 text-xs font-normal text-texto">{f.titular ?? 'Titular no informado'}</p>
                <p className="mt-1 text-[11.5px] font-normal text-gris">{f.comunas.join(', ') || f.region} · {fechaCL(f.fechaPresentacion)}{f.tipologia ? ` · ${f.tipologia}` : ''}</p>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <Ayuda texto="Abre la ficha oficial del expediente en el sitio del SEA." toque={false}><a href={f.urlFicha} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-azul-profundo hover:underline">Ver en e-SEIA ↗</a></Ayuda>
                  <BotonAgregar f={f} />
                </div>
              </li>
            ))}
          </ul>

          {/* Desktop: tabla compacta */}
          <div className="mt-3 hidden max-h-[55vh] overflow-y-auto rounded-[10px] border border-slate-200 sm:block">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-[#F8FAFC]">
                <tr className="border-b border-slate-300">
                  <th className="th">Proyecto</th>
                  <th className="th">Titular</th>
                  <th className="th">Comunas</th>
                  <th className="th whitespace-nowrap">Presentación</th>
                  <th className="th">Tipología</th>
                  <th className="th"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filas.map((f) => (
                  <tr key={f.expedienteId} className="hover:bg-celeste/[0.08]">
                    <td className="td font-medium text-azul-noche">
                      {f.nombre}
                      <Ayuda texto="Abre la ficha oficial del expediente en el sitio del SEA." toque={false} className="mt-0.5 !flex"><a href={f.urlFicha} target="_blank" rel="noopener noreferrer" className="text-[11.5px] font-medium text-azul-profundo hover:underline">Ver en e-SEIA ↗</a></Ayuda>
                    </td>
                    <td className="td text-texto">{f.titular ?? '—'}</td>
                    <td className="td text-texto">{f.comunas.join(', ') || f.region}</td>
                    <td className="td whitespace-nowrap tabular-nums text-texto">{fechaCL(f.fechaPresentacion)}</td>
                    <td className="td text-gris">{f.tipologia ?? '—'}</td>
                    <td className="td text-right"><BotonAgregar f={f} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <button type="button" onClick={onCerrar} className="btn-fantasma">Cerrar</button>
      </div>
    </Modal>
  );
}
